import FreshnessIndicator from '../../../components/FreshnessIndicator';
import { formatTimeAgo } from '../utils/formatters';

interface KPIMetricsProps {
  polymarketPrice: number | null;
  polymarketChange24h: number | null;
  lastOddsUpdate: string | null;
  priceGap: number | null;
  isLive?: boolean;
  betType: string | null;
}

/**
 * KPI Metrics section showing three key performance indicators:
 * - Polymarket Probability
 * - Odds Data freshness
 * - Price Gap
 */
export default function KPIMetrics({
  polymarketPrice,
  polymarketChange24h,
  lastOddsUpdate,
  priceGap,
  isLive,
  betType,
}: KPIMetricsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Polymarket Implied Probability KPI */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-sm text-gray-400 uppercase tracking-wide mb-1">Polymarket Probability</div>
            {polymarketPrice !== null ? (
              <>
                <div className="text-3xl font-bold text-purple-400">
                  {(polymarketPrice * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  YES token: ${polymarketPrice.toFixed(2)}
                </div>
                {polymarketChange24h !== null && (
                  <div className={`text-sm mt-1 ${polymarketChange24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {polymarketChange24h >= 0 ? '▲' : '▼'} {Math.abs(polymarketChange24h).toFixed(1)}% (24h)
                  </div>
                )}
              </>
            ) : betType ? (
              // Show bet type info even without prices
              <>
                <div className="text-lg text-yellow-400">Awaiting prices</div>
                <div className="text-xs text-gray-500 mt-1">
                  {betType}
                </div>
              </>
            ) : (
              <div className="text-lg text-gray-500">No match</div>
            )}
          </div>
        </div>
      </div>

      {/* Odds Freshness KPI */}
      <div className="card">
        <div className="text-sm text-gray-400 uppercase tracking-wide mb-1">Odds Data</div>
        <div className="flex items-center gap-3 mt-2">
          <FreshnessIndicator
            lastUpdate={lastOddsUpdate}
            label=""
            showLabel={false}
            warningThreshold={isLive ? 120 : 360}
            staleThreshold={isLive ? 300 : 600}
          />
          <div className="text-lg font-medium">
            {lastOddsUpdate ? formatTimeAgo(lastOddsUpdate) : 'No updates'}
          </div>
        </div>
      </div>

      {/* Price Gap KPI */}
      <div className="card">
        <div className="text-sm text-gray-400 uppercase tracking-wide mb-1">Price Gap</div>
        {priceGap !== null ? (
          <>
            <div className={`text-3xl font-bold ${Math.abs(priceGap) >= 3 ? 'text-green-400' : 'text-gray-400'}`}>
              {priceGap >= 0 ? '+' : ''}{priceGap.toFixed(1)}%
            </div>
            <div className="text-sm mt-1 text-gray-400">
              {Math.abs(priceGap) >= 3 ? 'Edge detected' : 'Within range'}
            </div>
          </>
        ) : (
          <div className="text-lg text-gray-500">Calculating...</div>
        )}
      </div>
    </div>
  );
}
